import { SelectableHeatMapFeature } from 'app/map/services/heatmap/types/SelectableHeatMapFeature';
import { LocationLookup } from 'app/shared/selection/locations/LocationLookup';
import { AbstractGeoDataMap } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { IMSVisualiser } from 'app/views/visualiser';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { Cesium } from 'app/cesium';
import { dataByLocationToGrid, GridSquare } from 'app/map/services/heatmap/types/choropleth_common';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { DataWithLocationArray } from 'app/shared/collections/time/ims_time_collection.factory';

interface Choropleth extends SelectableHeatMapFeature {
    // Values in degrees
    north: number;
    south: number;
    east: number;
    west: number;
}

type ChoroplethMap = Choropleth[];

export function createPrimitiveChoroplethMap(
    id: number,
    data: DataWithLocationArray,
    imsVisualiser: IMSVisualiser,
    imsAggregator: IMSAggregator,
): AbstractGeoDataMap {
    return new PrimitiveChoroplethMap(id, data, imsVisualiser, imsAggregator);
}

class PrimitiveChoroplethMap extends AbstractGeoDataMap {
    private _choropleths: Choropleth[];
    private _deleteFunc: () => void;
    private _imsVisualiser: IMSVisualiser;
    private _imsAggregator: IMSAggregator;
    private _statType: string;
    private _locationLookup: LocationLookup<Choropleth>;

    constructor(
        id: number,
        dataByLocation: DataWithLocationArray,
        imsVisualiser: IMSVisualiser,
        imsAggregator: IMSAggregator,
    ) {
        super(id, dataByLocation);
        this._imsVisualiser = imsVisualiser;
        this._imsAggregator = imsAggregator;
        const gridSquares = dataByLocationToGrid(dataByLocation);
        this._choropleths = gridSquaresToChoroplethMap(
            gridSquares,
            this._imsVisualiser,
            this._imsAggregator,
        );
        this._locationLookup = new LocationLookup<Choropleth>(this._choropleths);
        const collection = imsVisualiser.valueGetter.statCollection;
        this._statType = collection.getImsType();
    }

    delete(): void {
        if (this._deleteFunc) {
            this._deleteFunc();
            this._deleteFunc = null;
        }
    }

    drawOnViewer(viewer: Cesium.Viewer) {
        // Remove if it already exists
        this.delete();
        this._deleteFunc = drawChoroplethMapToPrimitives(this.id, viewer, this._choropleths);
    }

    /**
     *
     * @param {number} featureId - aka grid square ID
     * @returns {any}
     */
    getSelectableFeature(featureId: number): SelectableHeatMapFeature {
        return this._choropleths[featureId];
    }

    lookupSelectableFeatures(latitude: number, longitude: number): SelectableHeatMapFeature[] {
        // Choropleth maps should only have 1 'pixel' selectable at a time because there is
        // no overlap
        return this._locationLookup.lookup(latitude, longitude, 1, Infinity);
    }

    get statType(): string {
        return this._statType;
    }
}

/**
 * This is just a 1 to 1 mapping from grid square to choropleth. There is no need for a 2
 * stage grid with primitives. The original grid is created small enough already.
 * @param {GridSquare[]} gridSquares
 * @param {IMSVisualiser} imsVisualiser
 * @param {IMSAggregator} imsAggregator
 * @returns {ChoroplethMap}
 */
function gridSquaresToChoroplethMap(
    gridSquares: GridSquare[],
    imsVisualiser: IMSVisualiser,
    imsAggregator: IMSAggregator,
): ChoroplethMap {
    const colorRenderer = imsVisualiser.colorRenderer;
    return gridSquares.map((square: GridSquare, index: number) => {
        const statArray = square.datums;
        const value = imsAggregator.getAggValueForStatArray(statArray, imsVisualiser.valueGetter);
        const color = colorRenderer.getColor(value);
        return {
            id: index,
            latitude: square.north - (square.north - square.south) / 2,
            longitude: square.west + (square.east - square.west) / 2,
            // TODO: fill this stuff in if we decide to use primitives
            featureDataList: [],
            datums: square.datums,
            north: square.north,
            south: square.south,
            east: square.east,
            west: square.west,
            appearance: {
                color: color,
            },
            featureRadiusMetres: 20, // arbitrary value
        };
    });
}

function drawChoroplethMapToPrimitives(
    id: number,
    viewer: Cesium.Viewer,
    choroplethMap: ChoroplethMap,
): () => void {
    const primitives = viewer.scene.primitives;
    // Build all the instances for primitive batching.
    const geometryInstances = [];
    choroplethMap.forEach((choropleth) =>
        geometryInstances.push(choroplethToGeometryInstance(choropleth)),
    );

    // Add the primitive to the scene
    const primitive = primitives.add(
        new Cesium.GroundPrimitive({
            geometryInstances,
            appearance: new Cesium.PerInstanceColorAppearance(),
        }),
    );

    // Add our fake entity and lookup function to help with selects
    primitive.entity = {
        getId: () => id,
        selectionType: SelectionType.HEAT_MAP,
    };

    // Destroy the primitive
    return function () {
        primitives.remove(primitive);
    };
}

function choroplethToGeometryInstance(choropleth: Choropleth) {
    const rect = Cesium.Rectangle.fromDegrees(
        choropleth.west,
        choropleth.south,
        choropleth.east,
        choropleth.north,
    );

    const rectGeom = new Cesium.RectangleGeometry({
        rectangle: rect,
        vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT,
    });

    return new Cesium.GeometryInstance({
        geometry: rectGeom,
        attributes: {
            color: Cesium.ColorGeometryInstanceAttribute.fromColor(choropleth.appearance.color),
        },
    });
}
